import React, { createContext, useContext, useState, useEffect } from 'react';
import { siteSettingsAPI } from '../services/api';
import { generateColorShades, FONT_STACKS } from '../utils/colorShades';

const SiteSettingsContext = createContext(null);

export const useSiteSettings = () => {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return ctx;
};

const DEFAULT_SETTINGS = {
  site_name: 'ACAR',
  tagline: 'Accra Centre for Applied Research',
  primary_color: '#093862',
  font_choice: 'newsreader',
};

const STYLE_TAG_ID = 'acar-site-settings-overrides';

const applySettingsToDocument = (settings) => {
  const shades = generateColorShades(settings.primary_color) || generateColorShades(DEFAULT_SETTINGS.primary_color);
  const fontPair = FONT_STACKS[settings.font_choice] || FONT_STACKS.newsreader;

  const cssVars = Object.entries(shades)
    .map(([shade, hex]) => `--acar-navy-${shade}: ${hex};`)
    .join(' ');

  let styleTag = document.getElementById(STYLE_TAG_ID);
  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = STYLE_TAG_ID;
    document.head.appendChild(styleTag);
  }
  styleTag.textContent = `
    :root { ${cssVars} }
    body { font-family: ${fontPair.body}; }
    h1, h2, h3, h4, h5, h6 { font-family: ${fontPair.heading}; }
  `;

  document.title = settings.tagline
    ? `${settings.site_name} - ${settings.tagline}`
    : settings.site_name;
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await siteSettingsAPI.get();
      setSettings(res.data);
      applySettingsToDocument(res.data);
    } catch (e) {
      // If settings can't be loaded (e.g. backend briefly unreachable),
      // fall back to defaults rather than leaving the page unstyled --
      // the CSS variable fallbacks in tailwind.config.js already cover
      // this too, so this is a belt-and-suspenders default.
      applySettingsToDocument(DEFAULT_SETTINGS);
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    await loadSettings();
  };

  return (
    <SiteSettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};